import {
  QueryMetrics,
  UtilMetrics,
} from "pages/DevelopWorkspace/Editor/Document/DocumentOutput/EngineMonitoring/types";

export type EditorState = {
  doc: string;
  selection: {
    ranges: Array<{
      anchor: number;
      head: number;
    }>;
  };
  history: any;
  original: string | null;
};

export type LocalScript = {
  title: string;
  id: string;
  createdAt: string; // timestamp or date string
  editorState: EditorState;
};

export type StoredLocalScript = LocalScript & {
  content?: string;
};

export type RemoteScript = {
  title: string;
  id: string;
  createdAt: string; // timestamp or date string
  editorState: EditorState;
};

export type StoredRemoteScript = RemoteScript & {
  content?: string;
};

export enum QueryStatementStatus {
  running = "running",
  pending = "pending", // default
  success = "success",
  error = "error",
  cancelled = "cancelled",
  unknown = "unknown",
  restoring = "restoring",
  suspended = "suspended",
}

export type Statistics = {
  executionTimeSec: number;
  rowsRead: number;
  bytesRead: number;
  scannedBytesCache: number;
  scannedBytesStorage: number;
};

type PerformanceHint = {
  issue_description: string;
  issue_details: string;
  hint: string;
};

export type QueryStatementResult = {
  data?: any[];
  meta?: any[];
  rows: number;
  performanceHints?: PerformanceHint[];
};

export type StatementLocation = {
  lineNumber: number;
  start: number;
  end: number;
};

export type QueryStatement = {
  content: string;
  prevStatus?: QueryStatementStatus;
  status: QueryStatementStatus;
  id: string; // ui-generated id
  responseStatusCode: number | null; // TODO do we need it?
  error: any | null;
  statistics: Statistics | null;
  location: StatementLocation;
  resultRows?: number;
  explainType?: ExplainType;
  executionType?: ExecutionType;
  explain?: Partial<Record<ExplainType, any>>;
  serverQueryId?: string;
  scrollOffset?: number;
  filters?: Record<string, unknown>;
  sortConfig?: {
    columnIndex: number;
    sortOrder: string;
  };
  interactionState?: any;
  sentToEngineTimestamp?: number;
  hadNetworkError?: boolean;
};

export enum ExplainType {
  Physical = "physical",
  Logical = "logical",
  Analyze = "analyze",
}

export enum ExecutionType {
  Query = "query",
  Explain = "explain",
  Generated = "generated",
}

export enum CancellationStatus {
  Cancelled = "Cancelled",
  Initiated = "Initiated",
  Pending = "Pending",
  Failure = "Failure",
}

export type Execution = {
  executionType: ExecutionType;
  activeQueryStatementIndex: number;
  userSelectedActiveQueryStatementIndexTimestamp: number;
  queryStatements: QueryStatement[];
  executionTimestamp?: number | undefined;
  // It's an error on document level, not on query statement. The field is relevant for errors that happened before document execution.
  documentExecutionError: DocumentExecutionError | null;
  cancellationStatus?: CancellationStatus;
};

export enum ScriptType {
  local = "local",
  remote = "remote",
}

export type ExecutionContext = {
  database: { name: string | Symbol; isUserSelected?: boolean };
  engine: {
    name: string;
    isUserSelected?: boolean;
  };
  settings: { [key: string]: string };
  machineGeneratedSettings: { [key: string]: string };
};

export type DocumentExecutionError = {
  title: string;
  description: string;
};

export enum DocumentOutputTab {
  Results = "results",
  Statistics = "statistics",
  QueryHistory = "queryHistory",
  EngineMonitoring = "engineMonitoring",
  Explain = "explain",
  Empty = "empty",
}

export type DocumentLayout = {
  editorHeightPx: number;
  activeOutputTab: DocumentOutputTab;
  chatWidthPx: number;
};

export enum EngineMonitoringInterval {
  FiveMinutes = "5 minutes",
  OneHour = "1 hour",
  OneDay = "1 day",
  OneWeek = "1 week",
  OneMonth = "1 month",
}

export interface EngineMonitoringCustomTimeRange {
  start: Date;
  end: Date;
}

// null means "since engine start till now"
export type EngineMonitoringTimeRange =
  | EngineMonitoringCustomTimeRange
  | EngineMonitoringInterval
  | null;

export type DocumentEngineMonitoring = {
  timeRange: EngineMonitoringTimeRange;
  utilChart: {
    highlightedKeys: UtilMetrics[];
  };
  queriesChart: {
    highlightedKeys: QueryMetrics[];
  };
};

export enum AiStateExternalInitializer {
  FixQueryStatement = "fix-query-statement",
}

export type DocumentAiState = {
  text: string;
  pos: [number, number];
  widgetPosition: number;
  sql: string;
  aiResponse: string | null;
  autoRun: boolean;
  isLoading: boolean;
  lastError: string | null;
  externalInitializer: AiStateExternalInitializer | null;
  // If the user selected only a fragment inside a larger SQL statement, provide surrounding context
  // to guide LLM modifications accurately without losing structure around the selection
  wrappedText?: {
    before: string;
    after: string;
  };
};

export type DocumentAiStateUpdate = Partial<Omit<DocumentAiState, "autoRun">>;

export type DocumentAiStateInitialData = Omit<
  DocumentAiState,
  "aiResponse" | "isLoading" | "lastError"
>;

export type WorkspaceDocument = {
  context: ExecutionContext;
  id: string; // ui-generated id
  script: {
    type: ScriptType;
    id: string;
  };
  aiState: DocumentAiState | null;
  layout: DocumentLayout;
  execution: Execution | null;
  engineMonitoring: DocumentEngineMonitoring;
  createdAt: number;
  groupId: string | null;
};

export enum SortOrder {
  NewestFirst = "newestFirst",
  NewestLast = "newestLast",
}

export type TabGroup = {
  id: string;
  name: string;
  color: string; // css color, one of TAB_GROUP_COLORS
  collapsed: boolean;
};

export const TAB_GROUP_COLORS = [
  "#3f8a8a",
  "#4a7bb0",
  "#5a63a8",
  "#7a5f9e",
  "#96578c",
];

export type DocumentsState = {
  documents: WorkspaceDocument[];
  activeDocumentId: string | null;
  tabGroups: TabGroup[];
};

export const VISIBLE_QUERY_STATEMENTS_STATUSES = [
  QueryStatementStatus.success,
  QueryStatementStatus.error,
];

export const VISIBLE_QUERY_STATEMENTS_STATUSES_IN_RESULTS = [
  QueryStatementStatus.success,
  QueryStatementStatus.error,
  QueryStatementStatus.running,
  QueryStatementStatus.suspended,
];
